import { useRef, useCallback, useEffect } from 'react';

// Bottom sheet for mobile panes. Two modes:
//   • peek  , ~22vh, just enough to show header + first few items
//   • expand, ~80vh, full content with internal scroll
//
// Drag the handle up/down to change mode; tap to toggle.

const MONO = 'var(--mono)';
const PEEK_VH = 22;
const EXPAND_VH = 80;
const DRAG_THRESHOLD_PX = 40;

export default function MobileSheet({
  title,
  expanded,
  onToggle,
  children,
  tabBarHeightPx = 48,
  contentTopPx = 122,
  short = false,
}) {
  useEffect(() => {
    function key(e) { if (e.key === 'Escape' && expanded && !document.querySelector('[aria-modal="true"]')) onToggle(false); }
    window.addEventListener('keydown', key); return () => window.removeEventListener('keydown', key);
  }, [expanded, onToggle]);
  const startYRef = useRef(null);
  const movedYRef = useRef(0);
  const suppressClick = useRef(false);
  const sheetRef = useRef(null);

  const handleTouchStart = useCallback((e) => {
    startYRef.current = e.touches[0].clientY;
    movedYRef.current = 0;
  }, []);

  const handleTouchMove = useCallback((e) => {
    if (startYRef.current == null) return;
    movedYRef.current = e.touches[0].clientY - startYRef.current;
  }, []);

  const handleTouchEnd = useCallback(() => {
    if (startYRef.current == null) return;
    const moved = movedYRef.current;
    if (Math.abs(moved) > DRAG_THRESHOLD_PX) {
      // Up swipe → expand, down swipe → peek
      if (moved < 0 && !expanded) onToggle(true);
      else if (moved > 0 && expanded) onToggle(false);
      suppressClick.current = true;
    }
    startYRef.current = null;
    movedYRef.current = 0;
  }, [expanded, onToggle]);

  const heightVh = expanded ? EXPAND_VH : PEEK_VH;

  return (
    <div
      ref={sheetRef}
      className="og-mobile-sheet"
      style={{
        // The tab bar is tabBarHeightPx PLUS the home-indicator inset, anchor
        // above both or the sheet's bottom rows hide behind the bar on notched
        // phones.
        position: 'absolute',
        bottom: `calc(${tabBarHeightPx}px + env(safe-area-inset-bottom))`,
        left: 0, right: 0,
        height: !expanded && short ? 44 : `min(${heightVh}dvh, calc(100dvh - ${contentTopPx + tabBarHeightPx}px - env(safe-area-inset-top) - env(safe-area-inset-bottom)))`,
        background: 'var(--bg)',
        borderTop: '1px solid var(--hair)',
        borderTopLeftRadius: 10, borderTopRightRadius: 10,
        boxShadow: '0 -8px 30px rgba(0,0,0,0.6)',
        display: 'flex', flexDirection: 'column',
        transition: 'height 220ms cubic-bezier(0.4, 0, 0.2, 1)',
        pointerEvents: 'auto',
        overflow: 'hidden',
      }}
    >
      {/* Drag handle */}
      <button type="button" aria-expanded={expanded} aria-label={`${expanded ? 'Collapse' : 'Expand'} ${title} details`}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onClick={() => { if (suppressClick.current) { suppressClick.current = false; return; } onToggle(!expanded); }}
        style={{
          width:"100%", border:0, background:"transparent", color:"var(--txt)", height: 44, flexShrink: 0, // ≥ recommended touch height for the toggle/drag surface
          display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center',
          cursor: 'pointer',
          gap: 4,
        }}
      >
        <div style={{
          width: 40, height: 4, borderRadius: 2,
          background: 'var(--hair-2)',
        }} />
        {title && (
          <div role="heading" aria-level="2" style={{
            fontFamily: MONO, fontSize: 'var(--ui-small)', fontWeight: 700,
            color: 'var(--txt-dim)', letterSpacing: '0.18em',
            textTransform: 'uppercase',
          }}>{title}</div>
        )}
      </button>

      {/* Pane content (scrolls internally) */}
      <div inert={!expanded && short ? '' : undefined} aria-hidden={!expanded && short ? true : undefined} style={{
        flex: 1, overflowY: 'auto', overflowX: 'hidden',
        WebkitOverflowScrolling: 'touch',
        padding: '0 12px 12px',
      }}>
        {children}
      </div>
    </div>
  );
}
